import React, { useEffect, useMemo, useState } from 'react';
import { BadgeType } from 'src/components/ui/Badge/Badge';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import TabBar from 'src/components/layouts/TabBar/TabBar';
import Select, { Option } from 'src/components/ui/Select/Select';
import classNames from 'classnames';
import NotificationItem from 'src/components/ui/ListItems/Notification/Notification';
import { useTranslation } from 'react-i18next';
import useNotifications, { Notification as INotification } from 'src/services/notification';
import LoadingSkeleton from 'src/components/ui/LoadingSkeleton/LoadingSkeleton';
import { getUtilityModels } from 'src/services/utility';
import { getDetailViewUrl } from 'src/Layout';
import { useNavigate } from 'react-router-dom';
import Modal from '../../layouts/Modal/Modal';
import styles from './NotificationCenterModal.module.scss';

const TAB_INDEX: Record<string, number> = { unread: 0, read: 1, all: 2 };

enum NotificationCenterTab {
  UNREAD = 'unread',
  READ = 'read',
  ALL = 'all',
}

enum NotificationRating {
  WARNING = 'warning',
  INFO = 'info',
}

export type NotificationCenterModalProps = {
  isOpen: boolean;
  linkToResource?: boolean;
  onClose: () => void;
  onNotificationsUpdate: () => void;
};

const NotificationCenterModal: React.FC<NotificationCenterModalProps> = ({
  isOpen,
  onClose,
  onNotificationsUpdate,
  linkToResource = true,
}) => {
  const { t, i18n } = useTranslation();
  const {
    notifications,
    unreadCount,
    fetchUnreadCount,
    fetchMoreNotifications,
    setNotificationRead,
    loading,
    allNotificationsFetched,
    setSelectedType,
    setSelectedCategory,
    selectedType,
    selectedCategory,
    setAllNotificationsRead,
  } = useNotifications();

  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const navigate = useNavigate();

  const handleTabClick = (tab: NotificationCenterTab) => {
    setSelectedType(tab);
  };

  const onNotificationClick = async (notification: INotification) => {
    if (!notification.is_read) {
      await setNotificationRead(notification.id as number);
    }
    onNotificationsUpdate();
    if (linkToResource) {
      const path = getDetailViewUrl(notification.resource_id.model, notification.resource_id.id);
      if (path) {
        navigate(path);
        onClose();
      }
    }
  };

  useEffect(() => {
    const getCategoriesFilter = async () => {
      const models = await getUtilityModels();
      if (!models) return;
      setCategoryOptions(models);
    };

    getCategoriesFilter();
  }, [i18n.language]);

  useEffect(() => {
    if (!isOpen) return;
    fetchUnreadCount().then(count => {
      setSelectedType(count > 0 ? NotificationCenterTab.UNREAD : NotificationCenterTab.ALL);
    });
  }, [isOpen]);

  const tabs = useMemo(
    () => [
      {
        label: t('notifications.unread'),
        onClick: () => handleTabClick(NotificationCenterTab.UNREAD),
        entriesColor: BadgeType.Warning,
        badgeNumber: unreadCount,
      },
      {
        label: t('notifications.read'),
        onClick: () => handleTabClick(NotificationCenterTab.READ),
        entriesColor: BadgeType.Neutral,
      },
      { label: t('notifications.all'), onClick: () => handleTabClick(NotificationCenterTab.ALL) },
    ],
    [t, unreadCount],
  );

  const defaultValueCategory: Option = { value: '', label: t('notifications.category') };

  const markAllAsRead = async () => {
    await setAllNotificationsRead();
    onNotificationsUpdate();
  };

  const getNotificationText = () => {
    if (selectedCategory) {
      return t('notifications.no_notifications_in_filter');
    }
    if (selectedType === NotificationCenterTab.UNREAD) {
      return t('notifications.no_unread_notifications');
    }
    return t('notifications.no_notifications');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} hasBackdrop={false} hasCloseButton={false}>
      <div className={classNames(styles.root, '')}>
        <TabBar className="px-24 pt-20" tabs={tabs} defaultIndex={TAB_INDEX[selectedType] ?? 2} key={selectedType} />
        <div className={classNames(styles.notifications, 'overflow-x-hidden overflow-y-visible mt-24')}>
          {selectedType !== NotificationCenterTab.READ && unreadCount > 0 && (
            <Button
              tone={ButtonToneProps.SECONDARY}
              title={t('notifications.mark_all_as_read')}
              onClick={markAllAsRead}
              className="mb-16"
            />
          )}
          <div className="flex gap-x-8 mb-24">
            <Select
              options={categoryOptions}
              defaultValue={defaultValueCategory}
              icon="tune"
              onChange={e => setSelectedCategory(e?.value)}
              isClearable={!!selectedCategory}
            />
          </div>

          {!loading &&
            notifications.map(item => (
              <div className="mb-16 last:mb-0" key={item.id}>
                <NotificationItem
                  key={item.id}
                  packageId={item.resource_id.id}
                  reference={item.resource_id.reference}
                  name={categoryOptions.find(option => option.value === item.resource_id.model)?.label || ''}
                  details={item.message}
                  date={item.create_date}
                  read={item.is_read}
                  onClick={() => onNotificationClick(item)}
                  warning={item.rating === NotificationRating.WARNING}
                />
              </div>
            ))}
          {loading && (
            <div className="relative flex-1">
              <LoadingSkeleton numberOfItems={4} rounded type="cards" />
            </div>
          )}

          {!loading && notifications.length === 0 && (
            <div className="flex justify-center items-center h-64">
              <p className="text-center typo-text-14">{getNotificationText()}</p>
            </div>
          )}

          {/* Load more button for pagination */}
          {!allNotificationsFetched && notifications.length > 0 && (
            <div className="flex justify-center">
              <Button
                tone={ButtonToneProps.SECONDARY}
                title={t('notifications.load_more')}
                onClick={() => fetchMoreNotifications()}
                disabled={loading}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default NotificationCenterModal;
